import { useMemo } from 'react';
import { clsx } from 'clsx';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { differenceInMinutes, differenceInHours } from 'date-fns';

export type SlaStatus = 'on_track' | 'at_risk' | 'breached' | 'met' | 'none';

interface SlaIndicatorProps {
  dueAt?: string | Date | null;
  resolvedAt?: string | Date | null;
  showLabel?: boolean;
  showTimeRemaining?: boolean;
  size?: 'sm' | 'md' | 'lg';
  // Threshold in minutes for "at risk" status (default 30 minutes)
  atRiskThreshold?: number;
}

const STATUS_CONFIG: Record<SlaStatus, {
  icon: React.ElementType;
  label: string;
  className: string;
  iconClassName: string;
}> = {
  on_track: {
    icon: Clock,
    label: 'On Track',
    className: 'bg-green-50 text-green-700 border-green-200',
    iconClassName: 'text-green-500',
  },
  at_risk: {
    icon: AlertTriangle,
    label: 'At Risk',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    iconClassName: 'text-amber-500',
  },
  breached: {
    icon: XCircle,
    label: 'Breached',
    className: 'bg-red-50 text-red-700 border-red-200',
    iconClassName: 'text-red-500',
  },
  met: {
    icon: CheckCircle,
    label: 'Met',
    className: 'bg-green-50 text-green-700 border-green-200',
    iconClassName: 'text-green-500',
  },
  none: {
    icon: Clock,
    label: 'No SLA',
    className: 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-700',
    iconClassName: 'text-gray-400 dark:text-gray-500',
  },
};

const SIZE_CONFIG = {
  sm: {
    container: 'px-1.5 py-0.5 text-xs',
    icon: 'h-3 w-3',
    gap: 'gap-1',
  },
  md: {
    container: 'px-2 py-1 text-xs',
    icon: 'h-3.5 w-3.5',
    gap: 'gap-1.5',
  },
  lg: {
    container: 'px-3 py-1.5 text-sm',
    icon: 'h-4 w-4',
    gap: 'gap-2',
  },
};

/**
 * Exported so a consumer can render the status in its own palette without
 * duplicating the arithmetic. The components below are unchanged.
 */
export function calculateSlaStatus(
  dueAt: Date | null,
  resolvedAt: Date | null,
  atRiskThreshold: number
): { status: SlaStatus; timeRemaining: string | null } {
  if (!dueAt) {
    return { status: 'none', timeRemaining: null };
  }

  const now = new Date();

  // If resolved, check if it was before or after due date
  if (resolvedAt) {
    const resolved = new Date(resolvedAt);
    if (resolved <= dueAt) {
      return { status: 'met', timeRemaining: null };
    } else {
      return { status: 'breached', timeRemaining: null };
    }
  }

  // Check if breached
  if (now > dueAt) {
    const minutesOverdue = differenceInMinutes(now, dueAt);
    const hoursOverdue = differenceInHours(now, dueAt);

    let timeRemaining: string;
    if (hoursOverdue >= 24) {
      timeRemaining = `${Math.floor(hoursOverdue / 24)}d overdue`;
    } else if (hoursOverdue >= 1) {
      timeRemaining = `${hoursOverdue}h overdue`;
    } else {
      timeRemaining = `${minutesOverdue}m overdue`;
    }

    return { status: 'breached', timeRemaining };
  }

  // Check if at risk (within threshold)
  const minutesRemaining = differenceInMinutes(dueAt, now);
  if (minutesRemaining <= atRiskThreshold) {
    const timeRemaining = minutesRemaining <= 60
      ? `${minutesRemaining}m left`
      : `${Math.floor(minutesRemaining / 60)}h left`;
    return { status: 'at_risk', timeRemaining };
  }

  // On track
  const hoursRemaining = differenceInHours(dueAt, now);
  let timeRemaining: string;
  if (hoursRemaining >= 24) {
    timeRemaining = `${Math.floor(hoursRemaining / 24)}d left`;
  } else if (hoursRemaining >= 1) {
    timeRemaining = `${hoursRemaining}h left`;
  } else {
    timeRemaining = `${minutesRemaining}m left`;
  }

  return { status: 'on_track', timeRemaining };
}

export function SlaIndicator({
  dueAt,
  resolvedAt,
  showLabel = false,
  showTimeRemaining = false,
  size = 'md',
  atRiskThreshold = 30,
}: SlaIndicatorProps) {
  const { status, timeRemaining } = useMemo(() => {
    const due = dueAt ? new Date(dueAt) : null;
    const resolved = resolvedAt ? new Date(resolvedAt) : null;
    return calculateSlaStatus(due, resolved, atRiskThreshold);
  }, [dueAt, resolvedAt, atRiskThreshold]);

  const config = STATUS_CONFIG[status];
  const sizeConfig = SIZE_CONFIG[size];
  const Icon = config.icon;

  if (status === 'none') {
    return null;
  }

  return (
    <div
      className={clsx(
        'inline-flex items-center rounded-full border font-medium',
        sizeConfig.container,
        sizeConfig.gap,
        config.className
      )}
      title={`SLA: ${config.label}${timeRemaining ? ` (${timeRemaining})` : ''}`}
    >
      <Icon className={clsx(sizeConfig.icon, config.iconClassName)} />
      {showLabel && <span>{config.label}</span>}
      {showTimeRemaining && timeRemaining && (
        <span className="opacity-75">{timeRemaining}</span>
      )}
    </div>
  );
}

// Compact dot indicator for lists
export function SlaDot({
  dueAt,
  resolvedAt,
  atRiskThreshold = 30,
}: Pick<SlaIndicatorProps, 'dueAt' | 'resolvedAt' | 'atRiskThreshold'>) {
  const { status } = useMemo(() => {
    const due = dueAt ? new Date(dueAt) : null;
    const resolved = resolvedAt ? new Date(resolvedAt) : null;
    return calculateSlaStatus(due, resolved, atRiskThreshold);
  }, [dueAt, resolvedAt, atRiskThreshold]);

  if (status === 'none') {
    return null;
  }

  const dotColors: Record<SlaStatus, string> = {
    on_track: 'bg-green-500',
    at_risk: 'bg-amber-500',
    breached: 'bg-red-500',
    met: 'bg-green-500',
    none: 'bg-gray-300',
  };

  return (
    <span
      className={clsx('inline-block w-2 h-2 rounded-full', dotColors[status])}
      title={`SLA: ${STATUS_CONFIG[status].label}`}
    />
  );
}

// SLA countdown timer for detailed view
export function SlaCountdown({
  dueAt,
  resolvedAt,
  atRiskThreshold = 30,
}: Pick<SlaIndicatorProps, 'dueAt' | 'resolvedAt' | 'atRiskThreshold'>) {
  const { status, timeRemaining } = useMemo(() => {
    const due = dueAt ? new Date(dueAt) : null;
    const resolved = resolvedAt ? new Date(resolvedAt) : null;
    return calculateSlaStatus(due, resolved, atRiskThreshold);
  }, [dueAt, resolvedAt, atRiskThreshold]);

  if (status === 'none' || !dueAt) {
    return <span className="text-gray-400 text-sm dark:text-gray-500">No SLA</span>;
  }

  const config = STATUS_CONFIG[status];

  return (
    <div className={clsx('flex items-center gap-2 text-sm', config.className.split(' ').pop())}>
      <config.icon className={clsx('h-4 w-4', config.iconClassName)} />
      <div>
        <span className="font-medium">{config.label}</span>
        {timeRemaining && (
          <span className="ml-1 opacity-75">({timeRemaining})</span>
        )}
      </div>
    </div>
  );
}

export default SlaIndicator;
